/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  CustomTopbarButton,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

// Navigation links for Find Your Kind mobile menu
const MobileNavigationLinks = ({ currentPage }) => {
  return (
    <div className={css.mobileNavigationLinks}>
      <ExternalLink 
        href="https://findyourkind.com/"
        className={classNames(css.mobileNavLink, currentPage === 'LandingPage' ? css.currentPage : null)}
        target="_self"
      >
        Home
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/#about"
        className={css.mobileNavLink}
        target="_self"
      >
        About
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/#how-it-works"
        className={css.mobileNavLink}
        target="_self"
      >
        How it works
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/#contact"
        className={css.mobileNavLink}
        target="_self"
      >
        Contact us
      </ExternalLink>
      <ExternalLink
        href="https://app.findyourkind.com/s"
        className={css.mobileNavLink}
        target="_self"
      >
        Redeem voucher
      </ExternalLink>
      <ExternalLink 
        href="https://findyourkind.com/help-support/become-a-crew-member"
        className={css.mobileNavLink}
        target="_self"
      >
        Join the crew
      </ExternalLink>
    </div>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    history,
  } = props;

  const user = ensureCurrentUser(currentUser);

  // Check if user is super
  const userType = user?.attributes?.profile?.publicData?.userType;
  const isSuperUser = userType === 'super';

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // Custom button for mobile menu
  const customMobileButton = (
    <div className={css.customMobileButtonWrapper}>
      <a 
        href="https://findyourkind.com/help-support/become-a-crew-member"
        target="_self"
        className={css.customMobileButton}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          textDecoration: 'none'
        }}
      >
        Join the crew
      </a>
    </div>
  );

  if (!isAuthenticated) {
    // Use SignupForUserTypePage (not SignupPage): /signup is superUserOnly; buyer signup is /signup/customer-main.
    const signup = (
      <NamedLink
        name="SignupForUserTypePage"
        params={{ userType: 'customer-main' }}
        className={css.signupLink}
      >
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <MobileNavigationLinks currentPage={currentPage} />
          <div className={css.customLinksWrapper}>{extraLinks}</div>
          {customMobileButton}

          <div className={css.spacer} />
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const myServicesLinkMaybe =
    userType === 'provider-main' ? (
      <NamedLink
        className={classNames(css.navigationLink, currentPageClass('ProviderServicesPage'))}
        name="ProviderServicesPage"
      >
        <FormattedMessage id="TopbarMobileMenu.myServicesLink" />
      </NamedLink>
    ) : null;

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {myServicesLinkMaybe}
          {manageListingsLinkMaybe}
          {/* Provider Manager link - only for super users */}
          {isSuperUser && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('AdminPage'))}
              name="AdminPage"
            >
              <FormattedMessage id="TopbarMobileMenu.providerManagerLink" />
            </NamedLink>
          )}
          {isSuperUser && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('DashboardPage'))}
              name="DashboardPage"
            >
              <FormattedMessage id="TopbarMobileMenu.dashboardLink" />
            </NamedLink>
          )}
          {isSuperUser && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('FeaturesPage'))}
              name="FeaturesPage"
            >
              <FormattedMessage id="TopbarMobileMenu.featuresLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('TrackBalancePage'))}
            name="TrackBalancePage">
            <FormattedMessage id="TopbarMobileMenu.trackBalanceLink" />
          </NamedLink>
        </div>
        
        <MobileNavigationLinks currentPage={currentPage} />
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        {customMobileButton}
        <div className={css.spacer} />
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
